<template>
  <b-modal id="bv-modal-download-album-choice"
           size="md"
           centered
           hide-header
           :hide-footer="loading"
           @show="onShow">



      <div class="text-center">
        <Icon name="grDownload" :width="36" class="mb-3"/>
        <h1 class="modal-title mb-2">{{ $t('modal_download_album_choice.title') }}</h1>
      </div>

     <div v-if="loading" class="text-center py-3">
      <b-spinner small></b-spinner>
    </div>
      <div v-else class="mt-4">
        <p class="mb-2">{{ $t('modal_download_album_choice.choose_galleries') }}</p>
        <b-form-group class="mb-0">
          <b-form-checkbox
              v-for="item in flatTreeItems"
              :key="item.id"
              :checked="isItemFullySelected(item)"
              :indeterminate="isItemIndeterminate(item)"
              @change="checked => toggleItem(item, checked)"
              :style="{ marginLeft: (item.depth * 1.5) + 'rem' }"
              class="mb-2">
            {{ item.name }}
            <span v-if="item.count != null" class="text-secondary ml-1">({{ item.count }})</span>
          </b-form-checkbox>
        </b-form-group>
      </div>


    <template #modal-footer="{ cancel }">
      <div class="w-100">
        <b-button variant="primary" block @click="downloadEmail"
                  :disabled="loading || downloadDisabled" class="mb-2">
          <Icon name="send" :width="18" class="mr-1" vertical-align="sub"/>
          {{ $t('modal_download_album_choice.action_email') }}
        </b-button>
        <b-button variant="link" block @click="cancel()" class="text-secondary">
          {{ $t('modal_download_album_choice.action_cancel') }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import EventService from "@/services/Event";
import mediaService from "@/domain/galleries/service";
import galleryTreeService from "@/domain/galleryTree/service";
import {galleryTypes, mediaTypes} from "@/domain/galleries/constants";
import GalleryType from "@/domain/galleryTree/constants/galleryType";
import metric_message from "@/domain/metrics/message";

// valori di GALLERY_TAB_TYPE: non importiamo galleryTabType.js perché
// valuta i18n a livello di modulo e qui creerebbe un import circolare
const GALLERY_TAB_MEDIA = 'media'
const GALLERY_TAB_UNPURCHASED = 'unpurchased'

export default {
  name: "ModalDownloadAlbumChoice",


  props: {
    checkin: {
      type: Object,
      default: null,
    },
    galleryId: {
      type: Number,
      default: null,
    },
    galleries: {
      type: Array,
      default: null,
    },
    fetchGalleries: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: false,
      selectedGalleryIds: [],
      treeNodes: [],
    }
  },

  computed: {
    effectiveCheckin() {
      return this.checkin || this.$store.getters['checkinDetail/checkin']
    },
    flatTreeItems() {
      const items = []
      const walk = (nodes, depth) => {
        for (const node of nodes) {
          items.push({
            id: node.id,
            name: node.name,
            count: node.count,
            depth,
            leafIds: this.collectLeafIds(node),
          })
          if (node.children && node.children.length > 0) {
            walk(node.children, depth + 1)
          }
        }
      }
      walk(this.treeNodes, 0)
      return items
    },
    allLeafIds() {
      const ids = []
      const walk = (nodes) => {
        for (const node of nodes) {
          if (!node.children || node.children.length === 0) ids.push(node.id)
          else walk(node.children)
        }
      }
      walk(this.treeNodes)
      return ids
    },
    showCheckboxList() {
      return this.allLeafIds.length > 1
    },
    downloadDisabled() {
      return this.selectedGalleryIds.length === 0
    },
    defaultSelectedLeafIds() {
      // Solo le gallery private vanno preselezionate: le altre restano opt-in.
      const ids = []
      const walk = (nodes) => {
        for (const node of nodes) {
          if (!node.children || node.children.length === 0) {
            if (node.type === GalleryType.PRIVATE.value) ids.push(node.id)
          } else {
            walk(node.children)
          }
        }
      }
      walk(this.treeNodes)
      return ids
    },
  },

  methods: {
    downloadableCount(gallery) {
      // Le foto "not purchased" non sono scaricabili, quindi non vanno
      // conteggiate nel badge accanto al nome della gallery.
      const tabs = Array.isArray(gallery.tabs) ? gallery.tabs : []
      const unpurchased = tabs.find(tab => tab.type === GALLERY_TAB_UNPURCHASED)
      const total = gallery.num_media || 0
      return Math.max(0, total - ((unpurchased && unpurchased.count) || 0))
    },
    collectLeafIds(node) {
      if (!node.children || node.children.length === 0) return [node.id]
      const ids = []
      for (const child of node.children) {
        ids.push(...this.collectLeafIds(child))
      }
      return ids
    },
    isItemFullySelected(item) {
      return item.leafIds.length > 0 && item.leafIds.every(id => this.selectedGalleryIds.includes(id))
    },
    isItemIndeterminate(item) {
      if (item.leafIds.length <= 1) return false
      const selectedCount = item.leafIds.filter(id => this.selectedGalleryIds.includes(id)).length
      return selectedCount > 0 && selectedCount < item.leafIds.length
    },
    toggleItem(item, checked) {
      const ids = new Set(this.selectedGalleryIds)
      if (checked) item.leafIds.forEach(id => ids.add(id))
      else item.leafIds.forEach(id => ids.delete(id))
      this.selectedGalleryIds = [...ids]
    },
    async onShow() {
      this.treeNodes = []
      this.selectedGalleryIds = []
      if (this.effectiveCheckin) {
        try {
          this.loading = true
          this.treeNodes = this.effectiveCheckin.use_gallery_tree
              ? await this.fetchTreeGalleries()
              : await this.fetchFlatGalleries()
        } catch (_) {
          this.treeNodes = []
        } finally {
          this.loading = false
        }
      } else if (Array.isArray(this.galleries)) {
        this.treeNodes = this.buildFlatNodes(this.galleries)
      }
      this.selectedGalleryIds = this.defaultSelectedLeafIds
    },
    buildFlatNodes(list) {
      const seen = new Set()
      const nodes = []
      for (const g of list) {
        if (!g || g.id == null || seen.has(g.id)) continue
        seen.add(g.id)
        const count = g.count != null ? g.count
            : g.media_count != null ? g.media_count
                : g.num_media != null ? g.num_media
                    : null
        nodes.push({id: g.id, name: g.name, count, children: null})
      }
      return nodes
    },
    async fetchTreeGalleries() {
      const rootId = this.galleryId != null ? this.galleryId : 0
      return await this.buildSubtree(this.effectiveCheckin.id, rootId, true)
    },
    async buildSubtree(checkinId, parentId, isRoot = false) {
      const tree = await galleryTreeService.getGalleryTree(checkinId, parentId).catch(() => null)
      if (!tree) return []
      const subs = Array.isArray(tree.sub_gallery) ? tree.sub_gallery : []
      const built = await Promise.all(subs.map(async g => {
        if (!g || g.id == null) return null
        const count = this.downloadableCount(g)
        const node = {id: g.id, name: g.name, count, type: g.type, children: null}
        if (g.can_expand) {
          const children = await this.buildSubtree(checkinId, g.id)
          if (children.length > 0) {
            node.children = children
            return node
          }
        }
        return count > 0 ? node : null
      }))
      const filtered = built.filter(n => n !== null)
      if (isRoot && filtered.length === 0 && tree.id != null && this.downloadableCount(tree) > 0) {
        return [{id: tree.id, name: tree.name, count: this.downloadableCount(tree), type: tree.type, children: null}]
      }
      return filtered
    },
    async fetchFlatGalleries() {
      const response = await mediaService.getGalleries(this.effectiveCheckin.id)
      const group = response && response[galleryTypes.UNLOCKED]
      if (!Array.isArray(group)) return []
      const nodes = []
      const seen = new Set()
      const collect = list => {
        for (const g of list) {
          if (!g || g.id == null) continue
          if (g.media_count > 0 && !seen.has(g.id)) {
            seen.add(g.id)
            nodes.push({
              id: g.id,
              name: g.name || this.$t('pages.checkins_detail.default_gallery'),
              count: g.media_count,
              children: null,
            })
          }
          if (Array.isArray(g.sub_galleries)) collect(g.sub_galleries)
        }
      }
      collect(group)
      return nodes
    },
    async trackZipDownload(galleryIds) {
      try {
        const checkin = this.effectiveCheckin
        const zipId = window.crypto && window.crypto.randomUUID
            ? window.crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
        const medias = (await Promise.all(galleryIds.map(id => this.fetchGalleryMedias(checkin, id)))).flat()
        const seen = new Set()
        for (const media of medias) {
          if (!media || media.id == null || seen.has(media.id)) continue
          seen.add(media.id)
          metric_message.zipDownloaded(zipId, media.id, media.media_type === mediaTypes.VIDEO)
        }
      } catch (_) {
        console.error('trackZipDownload error', _)
        // il tracking non deve mai rompere il flusso di download
      }
    },
    async fetchGalleryMedias(checkin, galleryId) {
      // page_size richiesto al server; la dimensione effettiva resta quella
      // della prima risposta, così un eventuale cap lato server non tronca il loop
      const requestedPageSize = 100
      const medias = []
      let page = 1
      let firstPageSize = null
      let previousFirstId = null
      let hasMore = true
      while (hasMore) {
        const response = checkin.use_gallery_tree
            ? await galleryTreeService.getGalleryMedia(checkin.id, galleryId, {tab: GALLERY_TAB_MEDIA, page, page_size: requestedPageSize})
            : await mediaService.getMediasByGallery(checkin.id, galleryId, {page, page_size: requestedPageSize})
        const items = Array.isArray(response) ? response : (response && response.results) || []
        const firstId = items.length > 0 && items[0] ? items[0].id : null
        // se il server ignora il parametro page riceveremmo sempre la stessa pagina
        if (firstId != null && firstId === previousFirstId) break
        previousFirstId = firstId
        medias.push(...items)
        if (firstPageSize === null) firstPageSize = items.length
        const pagination = response && response.pagination
        hasMore = items.length > 0 && (pagination
            ? pagination.page_number < pagination.num_pages
            : items.length === firstPageSize)
        page += 1
      }
      return medias
    },
    downloadEmail() {
      this.loading = true
      const selection = this.showCheckboxList ? [...this.selectedGalleryIds] : this.galleryId
      EventService.downloadAlbum(this.effectiveCheckin.id, selection)
          .then(() => {
            this.trackZipDownload([...this.selectedGalleryIds])
            this.$bvToast.toast(
                this.$t('toast_download_album.text'),
                {
                  title: this.$t('toast_download_album.title'),
                  headerClass: 'd-block pt-2',
                  noCloseButton: false,
                  autoHideDelay: 5000
                })
            this.$bvModal.hide('bv-modal-download-album-choice')
          })
          .catch(error => {
            if (error.response && error.response.status === 400 &&
                (error.response.data.code === 'bulk_download_not_available' || error.response.data.code === 'bulk_download_missing_hd')) {
              this.$bvToast.toast(
                  this.$t('toast_download_album.error'),
                  {variant: 'warning', autoHideDelay: 8000, html: true})
            }
            this.$bvModal.hide('bv-modal-download-album-choice')
          }).finally(() => this.loading = false)
    },
  },
}
</script>
